import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { Dna, Sparkles, ChevronRight, ChevronLeft, Download, Share2, RotateCcw, PartyPopper, Music, Palette, Users, Wallet, Calendar, Camera, Facebook, Instagram, Sun, Moon, Lightbulb, Flame, Sprout, Speaker, Armchair, Headphones, Home as HomeIcon, Heart, Star } from "lucide-react";
import html2canvas from "html2canvas";
import { API_URL, LOGO_URL, BRAND_NAME } from "../../constants";
import { AIUpsellEngine } from "./AIUpsellEngine";
import PartyExperienceFlow from "./PartyExperienceFlow";
import LeadCaptureCard from "../LeadCaptureCard";

const STEPS = [
  {
    key: "vibe",
    question: "What's the vibe you're going for?",
    icon: Sparkles,
    options: [
      { label: "Chill & Intimate", value: "chill", desc: "Low-key, warm, cozy" },
      { label: "Wild & Energetic", value: "wild", desc: "Loud music, dance floor" },
      { label: "Elegant & Classy", value: "elegant", desc: "Sophisticated, premium" },
      { label: "Themed & Fun", value: "themed", desc: "Bollywood, retro, neon" },
      { label: "Minimal & Aesthetic", value: "minimal", desc: "Clean, Insta-worthy" },
    ],
  },
  {
    key: "guests",
    question: "How many guests are coming?",
    icon: Users,
    options: [
      { label: "Just Us (2-5)", value: "2-5", desc: "Intimate gathering" },
      { label: "Small Group (6-15)", value: "6-15", desc: "Close friends & family" },
      { label: "Medium Party (16-30)", value: "16-30", desc: "Proper party!" },
      { label: "Big Bash (30+)", value: "30+", desc: "Go big or go home" },
    ],
  },
  {
    key: "budget",
    question: "What's your budget range?",
    icon: Wallet,
    options: [
      { label: "Under 4K", value: "under 4000", desc: "Budget-friendly" },
      { label: "4K - 6K", value: "4000-6000", desc: "Good balance" },
      { label: "6K - 10K", value: "6000-10000", desc: "Premium setup" },
      { label: "10K - 15K", value: "10000-15000", desc: "Luxury experience" },
      { label: "15K+", value: "above 15000", desc: "No limits!" },
    ],
  },
  {
    key: "music",
    question: "What's your music mood?",
    icon: Music,
    options: [
      { label: "Bollywood Hits", value: "bollywood", desc: "Desi beats all night" },
      { label: "EDM / Pop", value: "edm", desc: "High energy drops" },
      { label: "Romantic / Soft", value: "romantic", desc: "Love songs & ballads" },
      { label: "Retro / Old School", value: "retro", desc: "90s & 2000s vibes" },
      { label: "Mix Everything", value: "mix", desc: "All genres, all vibes" },
    ],
  },
  {
    key: "occasion",
    question: "What's the occasion?",
    icon: Calendar,
    options: [
      { label: "Birthday", value: "Birthday", desc: "Happy Birthday!" },
      { label: "Anniversary", value: "Anniversary", desc: "Celebrating love" },
      { label: "Baby Shower", value: "Baby Shower", desc: "New beginnings" },
      { label: "Housewarming", value: "Housewarming", desc: "Welcome home" },
      { label: "Just Because!", value: "Just Because", desc: "Who needs a reason?" },
    ],
  },
];

const QuizStep = ({ step, value, onSelect }) => {
  const Icon = step.icon;
  return (
    <div className="space-y-5" data-testid={`party-dna-step-${step.key}`}>
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#E83E8C] to-[#7C3AED] flex items-center justify-center">
          <Icon size={20} className="text-white" />
        </div>
        <h3 className="text-lg md:text-xl font-bold text-[#1E1B4B]">{step.question}</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {step.options.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onSelect(opt.value)}
            className={`text-left p-4 rounded-2xl border-2 transition-all hover:scale-[1.02] ${
              value === opt.value
                ? "border-[#E83E8C] bg-[#E83E8C]/5 shadow-md"
                : "border-gray-200 hover:border-[#E83E8C]/40 bg-white"
            }`}
            data-testid={`dna-option-${step.key}-${opt.value}`}
          >
            <span className="font-semibold text-[#1E1B4B] text-sm">{opt.label}</span>
            <p className="text-xs text-[#475569] mt-0.5">{opt.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );
};

const PhotoUpload = ({ photo, onPhotoChange }) => {
  const fileRef = useRef(null);

  const handleFile = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = reader.result.split(",")[1];
      onPhotoChange(b64, reader.result);
    };
    reader.readAsDataURL(file);
  }, [onPhotoChange]);

  return (
    <div className="flex items-center gap-4 p-4 rounded-2xl border-2 border-dashed border-[#E83E8C]/30 bg-[#E83E8C]/5" data-testid="party-dna-photo-upload">
      <input type="file" ref={fileRef} accept="image/*" onChange={handleFile} className="hidden" />
      {photo ? (
        <img src={photo} alt="Your selfie" className="w-16 h-16 rounded-full object-cover border-2 border-[#E83E8C]" />
      ) : (
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#E83E8C]/20 to-[#7C3AED]/20 flex items-center justify-center border-2 border-dashed border-[#E83E8C]/40">
          <Camera size={24} className="text-[#E83E8C]" />
        </div>
      )}
      <div className="flex-1">
        <p className="text-sm font-semibold text-[#1E1B4B]">{photo ? "Looking great!" : "Add your selfie"}</p>
        <p className="text-xs text-[#475569]">Your photo will appear on the shareable card</p>
      </div>
      <button
        onClick={() => fileRef.current?.click()}
        className="px-4 py-2 rounded-full text-xs font-semibold text-[#E83E8C] border border-[#E83E8C] hover:bg-[#E83E8C]/5 transition-colors"
        data-testid="party-dna-upload-btn"
      >
        {photo ? "Change" : "Upload"}
      </button>
    </div>
  );
};

// Keyword-based icon mapping for the "Must-Haves" rows — colored tints
// mirror the Light reference and glow tints mirror the Dark reference.
const MUST_HAVE_STYLES = [
  { match: /light|lamp|led|fairy|string|lantern|bulb/i, Icon: Lightbulb, color: "#F59E0B", lightBg: "#FEF3C7", lightText: "#78350F", darkBg: "rgba(245,158,11,0.12)", darkRing: "rgba(245,158,11,0.4)" },
  { match: /cushion|pillow|throw|rug|couch|sofa|seat/i, Icon: Armchair, color: "#EC4899", lightBg: "#F3E8FF", lightText: "#581C87", darkBg: "rgba(236,72,153,0.12)", darkRing: "rgba(236,72,153,0.4)" },
  { match: /speaker|audio|sound|bluetooth|woofer|dj/i, Icon: Speaker, color: "#06B6D4", lightBg: "#FCE7F3", lightText: "#831843", darkBg: "rgba(6,182,212,0.12)", darkRing: "rgba(6,182,212,0.4)" },
  { match: /candle|diffuser|incense|aroma|scent|fragrance/i, Icon: Flame, color: "#A855F7", lightBg: "#FFEDD5", lightText: "#9A3412", darkBg: "rgba(168,85,247,0.12)", darkRing: "rgba(168,85,247,0.4)" },
  { match: /plant|green|flower|floral|leaf|fern|botanic|succulent/i, Icon: Sprout, color: "#10B981", lightBg: "#D1FAE5", lightText: "#064E3B", darkBg: "rgba(16,185,129,0.12)", darkRing: "rgba(16,185,129,0.4)" },
  { match: /music|vinyl|headphone|playlist/i, Icon: Headphones, color: "#8B5CF6", lightBg: "#EDE9FE", lightText: "#4C1D95", darkBg: "rgba(139,92,246,0.12)", darkRing: "rgba(139,92,246,0.4)" },
  { match: /party|balloon|confetti|decor|streamer/i, Icon: PartyPopper, color: "#F43F5E", lightBg: "#FFE4E6", lightText: "#881337", darkBg: "rgba(244,63,94,0.12)", darkRing: "rgba(244,63,94,0.4)" },
];
const DEFAULT_STYLE = { Icon: Sparkles, color: "#E83E8C", lightBg: "#FCE7F3", lightText: "#831843", darkBg: "rgba(232,62,140,0.12)", darkRing: "rgba(232,62,140,0.4)" };
const styleFor = (text = "") => MUST_HAVE_STYLES.find((s) => s.match.test(text)) || DEFAULT_STYLE;

// Decorative confetti/sparkle positions used in both themes (SVG only, no deps)
const CONFETTI = [
  { top: "6%", left: "8%", color: "#FF4D6D", shape: "line", rot: -30 },
  { top: "14%", left: "92%", color: "#A855F7", shape: "line", rot: 20 },
  { top: "22%", left: "4%", color: "#F59E0B", shape: "star" },
  { top: "26%", left: "95%", color: "#06B6D4", shape: "dot" },
  { top: "40%", left: "2%", color: "#EC4899", shape: "squiggle" },
  { top: "48%", left: "97%", color: "#F59E0B", shape: "triangle" },
  { top: "62%", left: "6%", color: "#8B5CF6", shape: "dot" },
  { top: "72%", left: "94%", color: "#10B981", shape: "line", rot: 45 },
  { top: "86%", left: "9%", color: "#F43F5E", shape: "squiggle" },
  { top: "90%", left: "90%", color: "#FACC15", shape: "star" },
];

const ConfettiLayer = ({ dark }) => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
    {CONFETTI.map((c) => {
      const op = dark ? 0.9 : 0.75;
      const common = { position: "absolute", top: c.top, left: c.left, opacity: op };
      const k = `${c.shape}-${c.top}-${c.left}-${c.color}`;
      if (c.shape === "line") return <span key={k} style={{ ...common, width: 18, height: 4, background: c.color, borderRadius: 2, transform: `rotate(${c.rot}deg)` }} />;
      if (c.shape === "dot") return <span key={k} style={{ ...common, width: 8, height: 8, background: c.color, borderRadius: "50%" }} />;
      if (c.shape === "triangle") return <span key={k} style={{ ...common, width: 0, height: 0, borderLeft: "7px solid transparent", borderRight: "7px solid transparent", borderBottom: `12px solid ${c.color}` }} />;
      if (c.shape === "star") return (
        <svg key={k} width="18" height="18" viewBox="0 0 24 24" style={common} fill={c.color}><path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 16.8 5.8 21.3l2.4-7.4L2 9.4h7.6z"/></svg>
      );
      if (c.shape === "squiggle") return (
        <svg key={k} width="22" height="10" viewBox="0 0 24 10" style={common} fill="none" stroke={c.color} strokeWidth="2" strokeLinecap="round"><path d="M1 5 Q 5 1, 9 5 T 17 5 T 23 5" /></svg>
      );
      return null;
    })}
  </div>
);

const HouseIllustration = ({ dark }) => (
  // Single SVG so the sparks share coordinates with the house — no more
  // overlay viewBox squishing the "safe zones" onto the roof.
  // House silhouette lives at x: 55-105, y: 36-92; sparks orbit outside it.
  <svg
    width="160"
    height="110"
    viewBox="0 0 160 110"
    aria-hidden="true"
    style={{ filter: dark ? "drop-shadow(0 0 10px rgba(236,72,153,0.55))" : "none" }}
  >
    {/* Celebratory sparks — 6 rays radiating OUTWARD from the house */}
    <g strokeWidth="3" strokeLinecap="round">
      <line x1="20"  y1="32" x2="36"  y2="18"  stroke="#FF4D6D" />
      <line x1="140" y1="32" x2="124" y2="18"  stroke="#8B5CF6" />
      <line x1="8"   y1="64" x2="30"  y2="64"  stroke="#FF4D6D" />
      <line x1="152" y1="64" x2="130" y2="64"  stroke="#8B5CF6" />
      <line x1="20"  y1="96" x2="36"  y2="104" stroke="#FF4D6D" />
      <line x1="140" y1="96" x2="124" y2="104" stroke="#8B5CF6" />
    </g>
    {/* House outline */}
    <path
      d="M55 60 L80 36 L105 60 L105 92 L55 92 Z"
      fill="none"
      stroke={dark ? "#C084FC" : "#1E1B4B"}
      strokeWidth="3.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {/* Heart inside the house, visually centred */}
    <path
      d="M80 69
         C 80 63, 90 63, 90 70
         C 90 76, 85 80, 80 83
         C 75 80, 70 76, 70 70
         C 70 63, 80 63, 80 69 Z"
      fill="#FF4D6D"
      stroke="#FF4D6D"
      strokeWidth="1"
    />
  </svg>
);

const DecorIllustration = ({ dark }) => {
  if (!dark) {
    // Flat pastel line icon matching the light reference
    return (
      <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: "#FCE7F3" }}>
        <svg width="38" height="38" viewBox="0 0 48 48" fill="none" stroke="#1E1B4B" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="4" y="24" width="28" height="14" rx="3" />
          <path d="M4 32 h28" />
          <path d="M8 38 v4 M28 38 v4" />
          <circle cx="38" cy="14" r="3" />
          <path d="M38 17 v15 M34 32 h8" />
          <path d="M44 20 c-2 2 -2 6 0 10 M44 20 c2 2 2 6 0 10" />
        </svg>
      </div>
    );
  }
  // 3D-style purple sofa+lamp+plant tableau for dark reference
  return (
    <div className="relative w-28 h-24 flex items-end justify-center">
      <div className="absolute w-16 h-20 rounded-full opacity-40" style={{ background: "radial-gradient(circle, #A855F7 0%, transparent 70%)", top: 0, left: "50%", transform: "translateX(-50%)" }} />
      <svg width="112" height="90" viewBox="0 0 112 90" fill="none">
        {/* Floor lamp */}
        <line x1="18" y1="30" x2="18" y2="80" stroke="#D4A574" strokeWidth="2.5" />
        <path d="M12 20 L24 20 L22 32 L14 32 Z" fill="#FEF08A" stroke="#F59E0B" strokeWidth="1.5" />
        <ellipse cx="18" cy="82" rx="6" ry="1.5" fill="#D4A574" />
        {/* Sofa */}
        <rect x="36" y="52" width="46" height="20" rx="5" fill="#C4B5FD" />
        <rect x="36" y="46" width="46" height="12" rx="4" fill="#A78BFA" />
        <rect x="34" y="56" width="6" height="18" rx="2" fill="#A78BFA" />
        <rect x="78" y="56" width="6" height="18" rx="2" fill="#A78BFA" />
        <rect x="52" y="48" width="8" height="8" rx="2" fill="#FACC15" />
        <line x1="40" y1="74" x2="40" y2="82" stroke="#4C1D95" strokeWidth="2" />
        <line x1="78" y1="74" x2="78" y2="82" stroke="#4C1D95" strokeWidth="2" />
        {/* Plant */}
        <rect x="92" y="62" width="14" height="14" rx="2" fill="#E5E7EB" />
        <path d="M99 62 C 94 54, 92 48, 95 42 M99 62 C 104 54, 106 48, 103 42 M99 62 C 99 54, 99 46, 99 40" stroke="#10B981" strokeWidth="2" fill="none" strokeLinecap="round" />
      </svg>
    </div>
  );
};

const MusicIllustration = ({ dark }) => {
  if (!dark) {
    return (
      <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: "#FFE4E6" }}>
        <svg width="36" height="36" viewBox="0 0 48 48" fill="none" stroke="#E11D48" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="18" cy="30" r="4" />
          <circle cx="34" cy="26" r="4" />
          <path d="M22 30 V12 L38 8 V26" />
          <path d="M42 10 l3 -3 M42 16 l5 0 M42 22 l3 3" stroke="#F43F5E" />
        </svg>
      </div>
    );
  }
  // 3D-style headphones + vinyl for dark reference
  return (
    <div className="relative w-28 h-24 flex items-center justify-center">
      <div className="absolute w-16 h-16 rounded-full opacity-40" style={{ background: "radial-gradient(circle, #06B6D4 0%, transparent 70%)" }} />
      <svg width="112" height="90" viewBox="0 0 112 90" fill="none">
        {/* Vinyl */}
        <circle cx="54" cy="54" r="24" fill="#1E1B4B" stroke="#8B5CF6" strokeWidth="1.5" />
        <circle cx="54" cy="54" r="18" fill="none" stroke="#4C1D95" strokeWidth="0.8" />
        <circle cx="54" cy="54" r="12" fill="none" stroke="#4C1D95" strokeWidth="0.8" />
        <circle cx="54" cy="54" r="5" fill="#F43F5E" />
        <circle cx="54" cy="54" r="1.5" fill="#1E1B4B" />
        {/* Headphones */}
        <path d="M30 48 C 30 28, 78 28, 78 48" stroke="#4C1D95" strokeWidth="3" fill="none" />
        <rect x="24" y="46" width="10" height="18" rx="3" fill="#6D28D9" />
        <rect x="74" y="46" width="10" height="18" rx="3" fill="#6D28D9" />
        <rect x="22" y="48" width="4" height="14" rx="2" fill="#8B5CF6" />
        <rect x="82" y="48" width="4" height="14" rx="2" fill="#8B5CF6" />
        {/* Music notes */}
        <g fill="#EC4899">
          <circle cx="86" cy="18" r="3" />
          <rect x="88" y="8" width="1.6" height="12" />
          <circle cx="96" cy="26" r="2.4" />
          <rect x="97.2" y="16" width="1.4" height="10" />
        </g>
      </svg>
    </div>
  );
};

const DoozyLogo = ({ dark }) => (
  // Same brand logo as the site header, always on a white pill so the mark
  // reads correctly on both light and dark Party DNA themes.
  <div
    className="inline-flex items-center select-none"
    aria-label={BRAND_NAME}
    style={{
      background: "#FFFFFF",
      padding: "6px 14px",
      borderRadius: 14,
      boxShadow: dark
        ? "0 2px 10px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.08)"
        : "0 2px 6px rgba(30,27,75,0.08), 0 0 0 1px rgba(30,27,75,0.06)",
    }}
  >
    <img
      src={LOGO_URL}
      alt={BRAND_NAME}
      crossOrigin="anonymous"
      style={{ height: 28, width: "auto", display: "block" }}
    />
  </div>
);

/**
 * AvatarWithSparkles — renders the user-uploaded selfie as a circular avatar
 * floating to the right of the hero title. Sparkles orbit around it and the
 * ring glows pink/purple on dark and soft rose on light.
 *
 * Positioned absolutely on tablet+ so it sits beside the centred hero title.
 * On mobile it falls back to a top-centre layout so the title stays readable.
 */
const AvatarWithSparkles = ({ photoPreview, dark }) => {
  if (!photoPreview) return null;

  const sparkleColor = dark ? "#F0ABFC" : "#E11D48";
  const sparkleGlow = dark ? "0 0 8px rgba(240,171,252,0.9)" : "0 0 6px rgba(225,29,72,0.5)";
  const ringGradient = dark
    ? "conic-gradient(from 0deg, #FF4D8D, #C084FC, #06B6D4, #FF4D8D)"
    : "conic-gradient(from 0deg, #FB7185, #F0ABFC, #FDBA74, #FB7185)";
  const haloShadow = dark
    ? "0 0 32px rgba(236,72,153,0.55), 0 0 60px rgba(139,92,246,0.35)"
    : "0 10px 28px rgba(236,72,153,0.25), 0 0 30px rgba(167,139,250,0.25)";

  // Six orbit positions — enough to feel festive without noise. We offset
  // the stars so they don't collide with the ring, and use mixed sizes.
  const sparkles = [
    { top: "-6%", left: "48%", size: 14, delay: "0s" },
    { top: "14%", left: "92%", size: 10, delay: "0.3s" },
    { top: "52%", left: "100%", size: 16, delay: "0.6s" },
    { top: "88%", left: "88%", size: 11, delay: "0.9s" },
    { top: "90%", left: "6%", size: 13, delay: "1.2s" },
    { top: "20%", left: "-8%", size: 10, delay: "1.5s" },
  ];

  return (
    <div
      className="pointer-events-none absolute right-2 md:right-6 top-0 md:-top-2 z-[5]"
      aria-hidden="true"
      data-testid="party-dna-avatar"
    >
      <div className="relative" style={{ width: 112, height: 112 }}>
        {/* Soft outer halo */}
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: dark
              ? "radial-gradient(circle, rgba(236,72,153,0.35) 0%, transparent 70%)"
              : "radial-gradient(circle, rgba(236,72,153,0.18) 0%, transparent 70%)",
            transform: "scale(1.3)",
          }}
        />
        {/* Gradient ring */}
        <div
          className="absolute inset-0 rounded-full"
          style={{ background: ringGradient, padding: 3, boxShadow: haloShadow }}
        >
          {/* Inner circle — uses BOTH background-image and <img> so we survive:
              1) html2canvas (reads background-image faithfully, ignores crossOrigin pitfalls)
              2) <img> fallback for accessibility + any browser that mis-sizes bg-image */}
          <div
            className="w-full h-full rounded-full overflow-hidden"
            style={{
              background: `${dark ? "#0B0F2A" : "#FFFFFF"} center/cover no-repeat`,
              backgroundImage: `url("${photoPreview}")`,
            }}
            data-pdna-avatar-inner="true"
          >
            <img
              src={photoPreview}
              alt=""
              className="w-full h-full object-cover"
              style={{ display: "block" }}
            />
          </div>
        </div>
        {/* Sparkles */}
        {sparkles.map((s) => (
          <Star
            key={`sparkle-${s.top}-${s.left}`}
            size={s.size}
            className="absolute party-dna-sparkle"
            style={{
              top: s.top,
              left: s.left,
              color: sparkleColor,
              fill: sparkleColor,
              filter: `drop-shadow(${sparkleGlow})`,
              animationDelay: s.delay,
            }}
          />
        ))}
      </div>
      {/* Scoped keyframes — twinkle = fade + light scale. */}
      <style>{`
        @keyframes partyDnaSparkleTwinkle {
          0%, 100% { opacity: 0.4; transform: scale(0.85) rotate(0deg); }
          50%      { opacity: 1;   transform: scale(1.15) rotate(20deg); }
        }
        .party-dna-sparkle {
          animation: partyDnaSparkleTwinkle 2.4s ease-in-out infinite;
          transform-origin: center;
        }
      `}</style>
    </div>
  );
};

const ResultCard = ({ personality, shareCardB64, shareCardDarkB64, shareCardLightB64, onRetake, answers, photoPreview }) => {
  const [showFlow, setShowFlow] = useState(false);
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "dark";
    return window.localStorage.getItem("party_dna_theme") || "dark";
  });
  const dark = theme === "dark";
  const shareRef = useRef(null);
  const [capturing, setCapturing] = useState(false);
  useEffect(() => {
    try { window.localStorage.setItem("party_dna_theme", theme); } catch (_) { /* ignore */ }
  }, [theme]);

  // Pick the backend share-card matching the current theme (used as a safety
  // fallback only — the primary download path screenshots the on-screen
  // template so what you see IS what you share).
  const activeCardB64 = (dark ? shareCardDarkB64 : shareCardLightB64) || shareCardB64 || shareCardDarkB64 || shareCardLightB64;

  // Capture the live template → PNG. Returns a Blob + a data URL.
  // Falls back to the backend-rendered card only if html2canvas blows up
  // (e.g. a tainted canvas on an exotic browser).
  const captureTemplate = useCallback(async () => {
    const node = shareRef.current;
    if (!node) return null;
    setCapturing(true);
    try {
      // Lock the capture to a fixed desktop-sized layout so the downloaded
      // card always has the same aspect ratio regardless of the viewer's
      // viewport. We render the clone at `TARGET_WIDTH` and tell html2canvas
      // to treat the cloned window as that same width so Tailwind `md:` and
      // responsive styles use the desktop branch — the visible page stays
      // untouched because html2canvas only mutates the cloned DOM.
      const TARGET_WIDTH = 1080; // 4:5-ish share-card aspect, like IG portrait
      const canvas = await html2canvas(node, {
        backgroundColor: null,
        useCORS: true,
        allowTaint: true,
        scale: 2,
        logging: false,
        windowWidth: TARGET_WIDTH,
        width: TARGET_WIDTH,
        // onclone runs on the cloned DOM BEFORE html2canvas renders. We use it to:
        //  1. Force the share root to the target width → stable aspect ratio.
        //  2. Replace the gradient-text title (background-clip:text + transparent
        //     fill isn't supported — would render as a solid gradient block on top
        //     of invisible text) with a flat solid colour pulled from the gradient.
        //  3. Hide any node still carrying data-html2canvas-ignore just in case
        //     the attribute got stripped somewhere upstream.
        onclone: (clonedDoc, clonedEl) => {
          try {
            // Lock the root clone to TARGET_WIDTH so even on narrow mobile
            // viewports the download renders at consistent desktop proportions.
            if (clonedEl) {
              clonedEl.style.width = `${TARGET_WIDTH}px`;
              clonedEl.style.maxWidth = `${TARGET_WIDTH}px`;
              clonedEl.style.margin = "0";
            }
            // Gradient-text titles (background-clip:text + transparent fill)
            // render as a solid gradient block in html2canvas — the text
            // itself vanishes. We swap the <h3> for an inline SVG that
            // *does* render gradient-filled text faithfully, so the
            // downloaded card matches the website.
            clonedDoc.querySelectorAll('[data-pdna-gradient-text="true"]').forEach((el) => {
              const text = el.textContent || "";
              const isDark = dark;
              if (!isDark) {
                // Light theme uses a flat colour — just reset the gradient styles.
                el.style.background = "none";
                el.style.webkitBackgroundClip = "unset";
                el.style.webkitTextFillColor = "unset";
                el.style.backgroundClip = "unset";
                el.style.color = "#1E1B4B";
                return;
              }
              // Dark theme — replace with gradient SVG text.
              const gid = `pdna-grad-${Math.random().toString(36).slice(2, 9)}`;
              const svgNS = "http://www.w3.org/2000/svg";
              const svg = clonedDoc.createElementNS(svgNS, "svg");
              svg.setAttribute("viewBox", "0 0 1000 110");
              svg.setAttribute("width", "100%");
              svg.setAttribute("height", "92");
              svg.style.display = "block";
              svg.style.margin = "0 auto 12px";

              const defs = clonedDoc.createElementNS(svgNS, "defs");
              const grad = clonedDoc.createElementNS(svgNS, "linearGradient");
              grad.setAttribute("id", gid);
              grad.setAttribute("x1", "0%");
              grad.setAttribute("y1", "0%");
              grad.setAttribute("x2", "100%");
              grad.setAttribute("y2", "0%");
              [["0%", "#FF4D8D"], ["50%", "#C084FC"], ["100%", "#8B5CF6"]].forEach(([off, col]) => {
                const s = clonedDoc.createElementNS(svgNS, "stop");
                s.setAttribute("offset", off);
                s.setAttribute("stop-color", col);
                grad.appendChild(s);
              });
              defs.appendChild(grad);
              svg.appendChild(defs);

              const t = clonedDoc.createElementNS(svgNS, "text");
              t.setAttribute("x", "500");
              t.setAttribute("y", "80");
              t.setAttribute("text-anchor", "middle");
              t.setAttribute("font-family", "Outfit, sans-serif");
              t.setAttribute("font-weight", "900");
              t.setAttribute("font-size", "88");
              t.setAttribute("fill", `url(#${gid})`);
              t.textContent = text;
              svg.appendChild(t);

              el.replaceWith(svg);
            });
            clonedDoc.querySelectorAll('[data-html2canvas-ignore="true"]').forEach((el) => {
              el.style.display = "none";
            });
          } catch (_) { /* ignore */ }
        },
      });
      const dataUrl = canvas.toDataURL("image/png", 0.95);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png", 0.95));
      return { blob, dataUrl };
    } catch (err) {
      // Fallback — use backend render if available.
      if (activeCardB64) {
        const dataUrl = `data:image/jpeg;base64,${activeCardB64}`;
        const blob = await fetch(dataUrl).then((r) => r.blob()).catch(() => null);
        return { blob, dataUrl };
      }
      return null;
    } finally {
      setCapturing(false);
    }
  }, [activeCardB64, dark]);

  const handleDownload = useCallback(async () => {
    const shot = await captureTemplate();
    if (!shot?.dataUrl) return;
    const link = document.createElement("a");
    link.href = shot.dataUrl;
    link.download = `party-dna-${(personality.personality_name || "card").replace(/\s+/g, "-").toLowerCase()}-${theme}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [captureTemplate, personality, theme]);

  const shareText = `My Party DNA is "${personality.personality_name}"! ${personality.short_description} Discover yours at www.doozygo.com`;

  const handleWhatsAppShare = useCallback(() => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank");
  }, [shareText]);

  const handleFacebookShare = useCallback(() => {
    const fbUrl = encodeURIComponent("https://www.doozygo.com");
    const fbQuote = encodeURIComponent(shareText);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${fbUrl}&quote=${fbQuote}`, "_blank", "width=600,height=400");
  }, [shareText]);

  const handleInstagramShare = useCallback(async () => {
    const shot = await captureTemplate();
    if (!shot?.blob) { await handleDownload(); return; }
    if (navigator.share) {
      try {
        const file = new File([shot.blob], "party-dna.png", { type: "image/png" });
        await navigator.share({ text: shareText, files: [file] });
        return;
      } catch { /* fallback below */ }
    }
    // Fallback — download so user can upload manually
    const link = document.createElement("a");
    link.href = shot.dataUrl;
    link.download = `party-dna-${theme}.png`;
    link.click();
  }, [captureTemplate, handleDownload, shareText, theme]);

  const mustHaves = useMemo(() => (personality.suggested_elements || []).slice(0, 5), [personality.suggested_elements]);

  // Theme-driven surface styles
  const surfaceBg = dark
    ? "linear-gradient(180deg, #0B0F2A 0%, #0E1133 55%, #0B0F2A 100%)"
    : "linear-gradient(180deg, #FDF5F7 0%, #FBEEF4 55%, #F7F3FA 100%)";
  const textPrimary = dark ? "#FFFFFF" : "#1E1B4B";
  const textSecondary = dark ? "rgba(255,255,255,0.75)" : "#475569";
  const cardBg = dark ? "linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)" : "#FFFFFF";
  const cardBorder = dark ? "1px solid rgba(139,92,246,0.3)" : "1px solid rgba(236,72,153,0.15)";
  const cardShadow = dark ? "0 8px 32px rgba(139,92,246,0.15), inset 0 1px 0 rgba(255,255,255,0.06)" : "0 2px 10px rgba(30,27,75,0.06)";

  return (
    <div
      ref={shareRef}
      className="relative rounded-3xl overflow-hidden"
      style={{ background: surfaceBg }}
      data-testid="party-dna-result"
      data-theme={theme}
    >
      {/* Corner glow blobs for dark */}
      {dark && (
        <>
          <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(236,72,153,0.35) 0%, transparent 65%)" }} />
          <div className="absolute -bottom-32 -right-20 w-96 h-96 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(14,165,233,0.25) 0%, transparent 65%)" }} />
          <div className="absolute top-1/3 -right-32 w-80 h-80 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(139,92,246,0.2) 0%, transparent 70%)" }} />
        </>
      )}
      {!dark && (
        <>
          <div className="absolute -top-20 -left-16 w-72 h-72 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(236,72,153,0.18) 0%, transparent 70%)" }} />
          <div className="absolute -bottom-24 -right-20 w-96 h-96 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(167,139,250,0.18) 0%, transparent 70%)" }} />
        </>
      )}

      <ConfettiLayer dark={dark} />

      <div className="relative z-10 px-6 md:px-10 pt-6 md:pt-8 pb-10">
        {/* Header: Logo + PARTY DNA pill + Theme toggle */}
        <div className="flex items-center justify-between mb-6">
          <DoozyLogo dark={dark} />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setTheme(dark ? "light" : "dark")}
              className="w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-105"
              style={{
                background: dark ? "rgba(255,255,255,0.08)" : "rgba(30,27,75,0.08)",
                border: dark ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(30,27,75,0.1)",
                color: dark ? "#FACC15" : "#4C1D95",
              }}
              aria-label={dark ? "Switch to light theme" : "Switch to dark theme"}
              data-testid="party-dna-theme-toggle"
              data-html2canvas-ignore="true"
            >
              {dark ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
              style={{
                background: dark ? "transparent" : "rgba(255,255,255,0.6)",
                border: dark ? "1.5px solid rgba(167,139,250,0.5)" : "1px solid rgba(30,27,75,0.1)",
                boxShadow: dark ? "0 0 10px rgba(139,92,246,0.3)" : "none",
              }}
            >
              {dark && <Dna size={14} style={{ color: "#C084FC" }} />}
              <span className="text-xs font-bold tracking-wider" style={{ color: dark ? "#E9D5FF" : "#1E1B4B" }}>PARTY DNA</span>
            </div>
          </div>
        </div>

        {/* Hero: house icon + title + description + user avatar */}
        <div className="relative mb-8">
          {photoPreview && <AvatarWithSparkles photoPreview={photoPreview} dark={dark} />}
          <div className="text-center">
            <div className="flex justify-center mb-2"><HouseIllustration dark={dark} /></div>
            <h3
              className="text-4xl md:text-5xl font-black tracking-tight mb-3"
              data-pdna-gradient-text="true"
              style={{
                fontFamily: "'Outfit', sans-serif",
                background: dark ? "linear-gradient(90deg, #FF4D8D 0%, #C084FC 50%, #8B5CF6 100%)" : "none",
                WebkitBackgroundClip: dark ? "text" : "unset",
                WebkitTextFillColor: dark ? "transparent" : "unset",
                color: dark ? undefined : textPrimary,
              }}
            >
              {personality.personality_name}
            </h3>
            <p className="text-sm md:text-base max-w-xl mx-auto leading-relaxed" style={{ color: textSecondary }}>
              {personality.short_description}
            </p>
          </div>
        </div>

        {/* Decor + Music cards */}
        <div className="grid md:grid-cols-2 gap-4 md:gap-5 max-w-2xl mx-auto mb-10">
          <div
            className="relative rounded-2xl p-5 md:p-6 overflow-hidden"
            style={{ background: cardBg, border: cardBorder, boxShadow: cardShadow, backdropFilter: dark ? "blur(10px)" : "none" }}
            data-testid="party-dna-decor-card"
          >
            {dark && <span className="absolute left-0 top-6 bottom-6 w-[3px] rounded-r" style={{ background: "linear-gradient(180deg, #FF4D8D, #8B5CF6)" }} />}
            <div className="flex justify-center mb-3"><DecorIllustration dark={dark} /></div>
            <h4 className="font-bold text-xl mb-1.5" style={{ color: textPrimary, fontFamily: "'Outfit', sans-serif" }}>Decor Style</h4>
            <p className="text-sm leading-relaxed" style={{ color: textSecondary }}>{personality.decor_style}</p>
          </div>
          <div
            className="relative rounded-2xl p-5 md:p-6 overflow-hidden"
            style={{ background: cardBg, border: cardBorder, boxShadow: cardShadow, backdropFilter: dark ? "blur(10px)" : "none" }}
            data-testid="party-dna-music-card"
          >
            {dark && <span className="absolute left-0 top-6 bottom-6 w-[3px] rounded-r" style={{ background: "linear-gradient(180deg, #06B6D4, #8B5CF6)" }} />}
            <div className="flex justify-center mb-3"><MusicIllustration dark={dark} /></div>
            <h4 className="font-bold text-xl mb-1.5" style={{ color: textPrimary, fontFamily: "'Outfit', sans-serif" }}>Music Vibe</h4>
            <p className="text-sm leading-relaxed" style={{ color: textSecondary }}>{personality.music_style}</p>
          </div>
        </div>

        {/* Must-Haves section */}
        <div className="max-w-xl mx-auto mb-8">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-10" style={{ background: dark ? "linear-gradient(90deg, transparent, #C084FC)" : "linear-gradient(90deg, transparent, #FF4D6D)" }} />
            <h4 className="font-bold text-lg md:text-xl" style={{ color: textPrimary, fontFamily: "'Outfit', sans-serif" }}>Your Party Must-Haves</h4>
            <span className="h-px w-10" style={{ background: dark ? "linear-gradient(90deg, #C084FC, transparent)" : "linear-gradient(90deg, #FF4D6D, transparent)" }} />
          </div>
          <div className="space-y-3">
            {mustHaves.length === 0 && (
              <p className="text-center text-sm" style={{ color: textSecondary }}>Your custom list is being crafted ✨</p>
            )}
            {mustHaves.map((elem, idx) => {
              const s = styleFor(elem);
              const Icon = s.Icon;
              return (
                <div
                  key={`${elem}-${idx}`}
                  className="flex items-center gap-4 rounded-full pr-5 pl-2 py-2 transition-all hover:translate-x-0.5"
                  style={{
                    background: dark ? s.darkBg : s.lightBg,
                    border: dark ? `1px solid ${s.darkRing}` : "none",
                    boxShadow: dark ? `0 0 14px ${s.darkRing}` : "none",
                  }}
                  data-testid={`party-dna-musthave-${idx}`}
                >
                  <div
                    className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
                    style={{
                      background: dark ? "rgba(0,0,0,0.3)" : "rgba(255,255,255,0.5)",
                      border: dark ? `1.5px solid ${s.color}` : "none",
                      boxShadow: dark ? `0 0 10px ${s.color}66, inset 0 0 6px ${s.color}33` : "none",
                    }}
                  >
                    <Icon size={20} style={{ color: s.color }} />
                  </div>
                  <span className="flex-1 text-sm md:text-base font-semibold" style={{ color: dark ? "#FFFFFF" : s.lightText }}>
                    {elem}
                  </span>
                  {dark && <ChevronRight size={18} style={{ color: s.color }} />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="relative max-w-xl mx-auto pt-6">
          <div className="flex items-center gap-4 mb-3">
            <span className="flex-1 h-px" style={{ background: dark ? "linear-gradient(90deg, transparent, rgba(192,132,252,0.5), transparent)" : "linear-gradient(90deg, transparent, rgba(30,27,75,0.2), transparent)" }} />
          </div>
          <div className="text-center">
            <p className="font-bold text-base md:text-lg" style={{ color: textPrimary, fontFamily: "'Outfit', sans-serif" }}>Discover your Party DNA</p>
            <a href="https://www.doozygo.com" className="text-sm font-semibold hover:underline" style={{ color: dark ? "#FF4D8D" : "#E11D48" }}>www.doozygo.com</a>
          </div>
        </div>

        {/* ---- Action buttons (kept) ---- */}

        <div className="flex items-center justify-center gap-3 flex-wrap mt-8" data-html2canvas-ignore="true">
          <button
            onClick={handleDownload}
            disabled={capturing}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-white text-sm bg-[#E83E8C] hover:bg-[#d12e7a] transition-all hover:scale-105 shadow-lg disabled:opacity-60"
            data-testid="party-dna-download"
          >
            <Download size={16} /> {capturing ? "Preparing…" : "Download"}
          </button>
          <button
            onClick={handleWhatsAppShare}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-white text-sm bg-[#25D366] hover:bg-[#1da851] transition-all hover:scale-105 shadow-lg"
            data-testid="party-dna-share-whatsapp"
          >
            <Share2 size={16} /> WhatsApp
          </button>
          <button
            onClick={handleFacebookShare}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-white text-sm bg-[#1877F2] hover:bg-[#1466D8] transition-all hover:scale-105 shadow-lg"
            data-testid="party-dna-share-facebook"
          >
            <Facebook size={16} /> Facebook
          </button>
          <button
            onClick={handleInstagramShare}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-white text-sm bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] hover:opacity-90 transition-all hover:scale-105 shadow-lg"
            data-testid="party-dna-share-instagram"
          >
            <Instagram size={16} /> Instagram
          </button>
        </div>

        <div className="flex items-center justify-center gap-3 mt-5 flex-wrap" data-html2canvas-ignore="true">
          <button
            onClick={onRetake}
            className="flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all hover:scale-[1.02]"
            style={{
              color: dark ? "#FFFFFF" : "#1E1B4B",
              border: dark ? "2px solid rgba(255,255,255,0.3)" : "2px solid #E5E7EB",
              background: dark ? "rgba(255,255,255,0.05)" : "#FFFFFF",
            }}
            data-testid="party-dna-retake"
          >
            <RotateCcw size={16} /> Retake Quiz
          </button>
          <button
            onClick={() => setShowFlow(true)}
            className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-bold text-white text-sm bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] hover:opacity-90 transition-all hover:scale-105 shadow-lg"
            data-testid="party-dna-book-now"
          >
            <Sparkles size={16} /> Plan My Party
          </button>
        </div>

        {/* Lead capture — pipe Party DNA viewers into the CRM */}
        <div className="max-w-md mx-auto mt-8" data-html2canvas-ignore="true">
          <LeadCaptureCard
            source="party_dna"
            extra={{
              personality: personality?.personality_name,
              decor_style: personality?.decor_style,
              music_style: personality?.music_style,
              vibe: answers?.vibe,
              budget: answers?.budget,
              guests: answers?.guests,
              occasion: answers?.occasion,
            }}
            headline={`Make "${personality?.personality_name}" your real party`}
            subheadline="Drop your number — we'll WhatsApp you a free quote tailored to your DNA within 24 hours."
            cta="Get My Party Quote"
          />
        </div>

        {/* Party Experience Flow / Upsell */}
        <div data-html2canvas-ignore="true">
          {showFlow ? (
            <div className="mt-8 pt-8" style={{ borderTop: dark ? "2px solid rgba(192,132,252,0.15)" : "2px solid rgba(232,62,140,0.1)" }}>
              <PartyExperienceFlow personality={personality} answers={answers} onClose={() => setShowFlow(false)} />
            </div>
          ) : (
            <AIUpsellEngine
              personality={personality}
              budget={answers?.budget}
              occasion={answers?.occasion}
              vibe={answers?.vibe}
              dark={dark}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export const PartyDNASection = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [photoB64, setPhotoB64] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleSelect = useCallback((value) => {
    const key = STEPS[currentStep].key;
    setAnswers((prev) => ({ ...prev, [key]: value }));
    if (currentStep < STEPS.length - 1) {
      setTimeout(() => setCurrentStep((s) => s + 1), 300);
    }
  }, [currentStep]);

  const handlePhotoChange = useCallback((b64, preview) => {
    setPhotoB64(b64);
    setPhotoPreview(preview);
  }, []);

  const handleGenerate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const body = { ...answers };
      if (photoB64) body.photo_b64 = photoB64;
      const res = await fetch(`${API_URL}/api/party-dna/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to generate");
      }
      const data = await res.json();
      setResult(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [answers, photoB64]);

  const handleRetake = useCallback(() => {
    setResult(null);
    setAnswers({});
    setPhotoB64(null);
    setPhotoPreview(null);
    setCurrentStep(0);
    setError(null);
  }, []);

  const allAnswered = STEPS.every((s) => answers[s.key]);
  const progress = ((Object.keys(answers).length) / STEPS.length) * 100;

  return (
    <section id="party-dna" className="py-20 md:py-28 bg-gradient-to-br from-[#1E1B4B] via-[#2d2860] to-[#1E1B4B] relative overflow-hidden" data-testid="party-dna-section">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#E83E8C] rounded-full blur-[120px]" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#7C3AED] rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
            <Dna size={18} className="text-[#E83E8C]" />
            <span className="text-white/80 text-sm font-medium">AI-Powered Quiz</span>
          </div>
          <h2 className="font-['Outfit'] text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Discover Your Party DNA
          </h2>
          <p className="text-white/60 text-base md:text-lg max-w-xl mx-auto">
            Answer 5 quick questions and our AI will reveal your unique party personality — plus the perfect decor style for you!
          </p>
        </div>

        <div className={result ? "rounded-3xl shadow-2xl overflow-hidden" : "bg-[#FFFAF5] rounded-3xl shadow-2xl p-6 md:p-10"}>
          {result ? (
            <ResultCard
              personality={result.personality}
              shareCardB64={result.share_card_b64}
              shareCardDarkB64={result.share_card_dark_b64}
              shareCardLightB64={result.share_card_light_b64}
              onRetake={handleRetake}
              answers={answers}
              photoPreview={photoPreview}
            />
          ) : (
            <div>
              <div className="mb-8">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs font-medium text-[#475569]">
                    Step {currentStep + 1} of {STEPS.length}
                  </span>
                  <span className="text-xs font-medium text-[#E83E8C]">
                    {Math.round(progress)}% complete
                  </span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#E83E8C] to-[#7C3AED] rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              <QuizStep
                step={STEPS[currentStep]}
                value={answers[STEPS[currentStep].key]}
                onSelect={handleSelect}
              />

              {/* Photo upload — show on last step */}
              {currentStep === STEPS.length - 1 && allAnswered && (
                <div className="mt-6">
                  <PhotoUpload photo={photoPreview} onPhotoChange={handlePhotoChange} />
                </div>
              )}

              <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setCurrentStep((s) => Math.max(0, s - 1))}
                  disabled={currentStep === 0}
                  className="flex items-center gap-1 text-sm font-medium text-[#475569] disabled:opacity-30 hover:text-[#1E1B4B] transition-colors"
                  data-testid="party-dna-prev"
                >
                  <ChevronLeft size={18} /> Back
                </button>

                {currentStep < STEPS.length - 1 ? (
                  <button
                    onClick={() => setCurrentStep((s) => Math.min(STEPS.length - 1, s + 1))}
                    disabled={!answers[STEPS[currentStep].key]}
                    className="flex items-center gap-1 px-6 py-2.5 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-[#E83E8C] to-[#7C3AED] disabled:opacity-40 hover:opacity-90 transition-all"
                    data-testid="party-dna-next"
                  >
                    Next <ChevronRight size={18} />
                  </button>
                ) : (
                  <button
                    onClick={handleGenerate}
                    disabled={!allAnswered || loading}
                    className="flex items-center gap-2 px-8 py-3 rounded-full text-sm font-bold text-white bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] disabled:opacity-40 hover:opacity-90 transition-all hover:scale-105 shadow-lg"
                    data-testid="party-dna-generate"
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Analyzing DNA...
                      </>
                    ) : (
                      <>
                        <Dna size={18} /> Reveal My Party DNA
                      </>
                    )}
                  </button>
                )}
              </div>

              {error && (
                <p className="text-center text-red-500 text-sm mt-4" data-testid="party-dna-error">{error}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
